import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const BannerContainer = styled.section`
  position: relative;
  height: 70vh;
  width: 100%;
  overflow: hidden;
  background: var(--dark-bg);
`;

const BannerSlide = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BannerImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  filter: brightness(0.6) saturate(1.2);
`;

const BannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(10,10,10,0.8) 0%,
    transparent 50%,
    rgba(10,10,10,0.8) 100%
  );
`;

const BannerContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;

  h2 {
    font-size: 4rem;
    font-weight: 800;
    margin-bottom: 1rem;
    text-transform: uppercase;

    span {
      background: linear-gradient(135deg, #d395ff, #ff7fd9, #b0ff64);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      display: inline-block;
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    font-size: 1.2rem;
    color: rgba(255,255,255,0.8);
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const BannerBadge = styled(motion.span)`
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background: linear-gradient(135deg, #d395ff, #ff7fd9);
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 30px rgba(211,149,255,0.3);
`;

const BannerButton = styled(motion.button)`
  padding: 1rem 3rem;
  background: transparent;
  border: 2px solid white;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: var(--dark-bg);
  }
`;

const NavButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 2rem;' : 'right: 2rem;'}
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255,255,255,0.3);
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
  z-index: 10;
`;

const Dot = styled(motion.button)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.active ? 'white' : 'rgba(255,255,255,0.3)'};
  border: 2px solid ${props => props.active ? '#d395ff' : 'transparent'};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;

  &:hover {
    background: white;
  }
`;

const ProductBanner = ({ products = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Banner products (use first 5 products or fallback)
  const bannerProducts = [
    {
      id: 1,
      name: "Premium Eclair",
      description: "Experience the magic of our signature center filled eclair toffee",
      category: "Signature",
      image: "/assets/product6.png",
      badge: "BESTSELLER"
    },
    {
      id: 2,
      name: "Assorted Eclairs",
      description: "Award-winning caramel eclair toffee handcrafted with pure butter",
      category: "Premium",
      image: "/assets/product7.png",
      badge: "AWARD WINNER"
    },
    {
      id: 3,
      name: "Assorted Candies",
      description: "Famous Coffe King, Boneto Candy, Milkoes, Pulp Candy ",
      category: "Luxury",
      image: "/assets/product8.png",
      badge: "LIMITED"
    },
    {
      id: 4,
      name: "Berry Burst",
      description: "A explosion of mixed berries in every bite, made with real fruit",
      category: "Fruity",
      image: "/assets/products/berry-burst.jpg",
      badge: "FAN FAVORITE"
    },
    {
      id: 5,
      name: "Mint Crystal",
      description: "Hand-pulled mint toffee with crystallized sugar for the perfect crunch",
      category: "Artisan",
      image: "/assets/products/mint-crystal.jpg",
      badge: "NEW"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % bannerProducts.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [bannerProducts.length]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % bannerProducts.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + bannerProducts.length) % bannerProducts.length);
  };

  return (
    <BannerContainer>
      <AnimatePresence mode="wait">
        <BannerSlide
          key={currentIndex}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.8 }}
        >
          <BannerImage src={bannerProducts[currentIndex].image} />
          <BannerOverlay />
          
          <BannerContent
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <BannerBadge
              animate={{
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity
              }}
            >
              {bannerProducts[currentIndex].badge}
            </BannerBadge>
            
            <h2>
              <span>{bannerProducts[currentIndex].name}</span>
            </h2>
            
            <p>{bannerProducts[currentIndex].description}</p>
            
            <BannerButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Discover More
            </BannerButton>
          </BannerContent>
        </BannerSlide>
      </AnimatePresence>

      <NavButton
        direction="left"
        onClick={handlePrev}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FiChevronLeft size={24} />
      </NavButton>

      <NavButton
        direction="right"
        onClick={handleNext}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FiChevronRight size={24} />
      </NavButton>

      <DotsContainer>
        {bannerProducts.map((_, index) => (
          <Dot
            key={index}
            active={index === currentIndex}
            onClick={() => setCurrentIndex(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </DotsContainer>
    </BannerContainer>
  );
};

export default ProductBanner;